import React, { useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Shield } from "lucide-react";

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy — The Patient Tech Bloke";
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#0F2E52]">
      <Header />

      <main data-testid="privacy-policy-page">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div aria-hidden="true" className="absolute inset-0 -z-10">
            <div className="absolute inset-0 bg-gradient-to-br from-[#F5FAEC] via-white to-[#F0F7E8]" />
            <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#6BAF3C]/15 blur-3xl" />
            <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#0F2E52]/10 blur-3xl" />
          </div>
          <div className="max-w-4xl mx-auto px-5 md:px-8 pt-8 md:pt-16 pb-6 md:pb-10 text-center">
            <span className="inline-flex items-center gap-2 bg-[#6BAF3C] text-white font-[Work_Sans] font-black text-[10px] md:text-sm uppercase tracking-widest rounded-full px-3 py-1 md:px-4 md:py-1.5 shadow-sm">
              <Shield className="w-3 h-3 md:w-3.5 md:h-3.5" strokeWidth={2.8} />
              Your privacy matters
            </span>
            <h1
              data-testid="privacy-heading"
              className="mt-3 md:mt-4 font-[Work_Sans] font-black tracking-tight text-[#0F2E52] leading-[1.02] text-3xl sm:text-5xl lg:text-6xl"
            >
              Privacy <span className="text-[#6BAF3C]">Policy</span>.
            </h1>
            <p className="mt-3 md:mt-4 text-sm md:text-lg text-[#334155] max-w-2xl mx-auto">
              The Patient Tech Bloke — Adelaide, South Australia.
            </p>
            <p className="mt-1 text-xs md:text-sm font-semibold text-[#475569]">
              Last updated: 3 November 2026
            </p>
          </div>
        </section>

        {/* Policy content */}
        <section className="pb-16 md:pb-24">
          <div className="max-w-3xl mx-auto px-5 md:px-8">
            <article className="rounded-2xl md:rounded-3xl bg-white border border-[#DCE4EC] p-6 md:p-10 shadow-sm space-y-7 md:space-y-8">

              <Section n="1" title="Introduction">
                The Patient Tech Bloke provides home technology assistance in
                Adelaide, South Australia. We respect your privacy and handle
                personal information responsibly.
              </Section>

              <Section n="2" title="Information We Collect">
                When you contact us through our website, Facebook, Instagram,
                telephone or other channels, we may collect your name, phone
                number, email address, details of your enquiry and information
                reasonably necessary to arrange or provide services.
              </Section>

              <Section n="3" title="How We Use Your Information">
                We use personal information to respond to enquiries, contact
                customers, arrange appointments, provide requested technology
                assistance and maintain necessary business records. We do not
                sell customer contact information.
              </Section>

              <Section n="4" title="Facebook and Instagram Enquiries">
                When you submit an enquiry through a Meta lead form, we receive
                the information you provide. We use it to respond to your
                request, generally between 9:00am and 7:00pm.
              </Section>

              <Section n="5" title="Information Security">
                We take reasonable steps to protect personal information against
                misuse, loss and unauthorised access or disclosure. Customer
                information may be stored in business email, administrative
                systems and services used to operate our business.
              </Section>

              <Section n="6" title="Sharing Information">
                We may disclose information to service providers when reasonably
                necessary to provide requested services, with your permission,
                or when required or authorised by law. Some service providers
                may store or process information outside Australia.
              </Section>

              <Section n="7" title="Access, Corrections and Complaints">
                You can contact us to request access to or correction of your
                personal information, or to raise a privacy concern. We will
                consider your request and respond within a reasonable period.
              </Section>

              <Section n="8" title="Retention">
                We retain information for as long as reasonably necessary for
                business purposes or legal obligations, and securely delete or
                de-identify it when no longer required where appropriate.
              </Section>

              <Section n="9" title="Contact Details">
                <div className="space-y-1">
                  <p><strong className="text-[#0F2E52]">The Patient Tech Bloke</strong></p>
                  <p>Adelaide, South Australia</p>
                  <p>
                    Website:{" "}
                    <a
                      href="https://thepatienttechbloke.com.au"
                      className="text-[#0F2E52] font-semibold underline underline-offset-4 hover:text-[#6BAF3C]"
                      data-testid="privacy-website-link"
                    >
                      https://thepatienttechbloke.com.au
                    </a>
                  </p>
                  <p>
                    Email:{" "}
                    <a
                      href="mailto:help@thepatienttechbloke.com.au"
                      className="text-[#0F2E52] font-semibold underline underline-offset-4 hover:text-[#6BAF3C]"
                      data-testid="privacy-email-link"
                    >
                      help@thepatienttechbloke.com.au
                    </a>
                  </p>
                </div>
              </Section>

              <Section n="10" title="Changes to This Policy">
                We may update this policy from time to time. The current version
                will be available on our website.
              </Section>

            </article>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section data-testid={`privacy-section-${n}`}>
      <h2 className="font-[Work_Sans] font-black text-lg md:text-2xl text-[#0F2E52] leading-tight">
        <span className="text-[#6BAF3C]">{n}.</span> {title}
      </h2>
      <div className="mt-2 md:mt-3 text-base md:text-lg leading-relaxed text-[#334155]">
        {children}
      </div>
    </section>
  );
}
